import type { Node, Edge } from '@xyflow/react';
import type { AdGraphResponseDto } from '@terra/shared-contracts-internal-network';
import {
  getAdGraphEdgeGroupKey,
  getAdGraphLinkDisplayLabel,
  InternalNetworkAdEntityKindSchema,
  isAdGraphCrownJewelNode,
  isAdGraphOwnedNode,
} from '@terra/shared-contracts-internal-network';
import type { InternalNetworkNodeData } from './constants';

const NODE_WIDTH = 180;
const NODE_WIDTH_COMPACT = 140;
const NODE_HEIGHT_WITH_LABEL = 110;
const NODE_HEIGHT_COMPACT = 72;
const NODE_HEIGHT_COMPACT_DENSITY = 88;

export type InternalNetworkGraphViewScope = 'full' | 'direct';

export function filterGraphToDirectNeighborhood({
  graph,
  centerNodeId,
}: {
  graph: AdGraphResponseDto;
  centerNodeId: string;
}): AdGraphResponseDto {
  const visibleNodeIds = new Set<string>([centerNodeId]);
  const visibleEdges = graph.edges.filter((edge) => {
    const touchesCenter =
      edge.sourceId === centerNodeId || edge.targetId === centerNodeId;
    if (touchesCenter) {
      visibleNodeIds.add(edge.sourceId);
      visibleNodeIds.add(edge.targetId);
    }
    return touchesCenter;
  });

  return {
    ...graph,
    nodes: graph.nodes.filter((node) => visibleNodeIds.has(node.id)),
    edges: visibleEdges,
  };
}

export function buildInternalNetworkFlowElements({
  graph,
  showNodeLabels = true,
  showEdgeLabels = graph.nodes.length <= 30,
  viewScope = 'full',
  centerNodeId = null,
  nodeDensity = 'default',
}: {
  graph: AdGraphResponseDto;
  showNodeLabels?: boolean;
  showEdgeLabels?: boolean;
  viewScope?: InternalNetworkGraphViewScope;
  centerNodeId?: string | null;
  nodeDensity?: 'default' | 'compact';
}) {
  const scopedGraph =
    viewScope === 'direct' && centerNodeId
      ? filterGraphToDirectNeighborhood({ graph, centerNodeId })
      : graph;

  const flowNodes: Node<InternalNetworkNodeData>[] = [];
  const flowEdges: Edge[] = [];
  const simplifyEdges = scopedGraph.nodes.length > 200;
  const isCompactDensity = nodeDensity === 'compact';
  const nodeWidth = isCompactDensity ? NODE_WIDTH_COMPACT : NODE_WIDTH;
  const nodeHeight = showNodeLabels
    ? isCompactDensity
      ? NODE_HEIGHT_COMPACT_DENSITY
      : NODE_HEIGHT_WITH_LABEL
    : NODE_HEIGHT_COMPACT;

  for (const node of scopedGraph.nodes) {
    const parsedKind = InternalNetworkAdEntityKindSchema.safeParse(node.kind);
    if (!parsedKind.success) {
      continue;
    }

    flowNodes.push({
      id: node.id,
      type: 'internalNetworkNode',
      width: nodeWidth,
      height: nodeHeight,
      position: { x: 0, y: 0 },
      data: {
        label: node.label,
        entityKind: parsedKind.data,
        objectId: node.objectId,
        distinguishedName: node.distinguishedName,
        isCrownJewel: isAdGraphCrownJewelNode(node),
        isOwned: isAdGraphOwnedNode(node),
        samAccountName: node.samAccountName,
        showNodeLabel: showNodeLabels,
        density: nodeDensity,
      },
    });
  }

  const edgesByGroup = new Map<string, typeof scopedGraph.edges>();
  for (const edge of scopedGraph.edges) {
    const groupKey = getAdGraphEdgeGroupKey({
      sourceId: edge.sourceId,
      targetId: edge.targetId,
    });
    const group = edgesByGroup.get(groupKey) ?? [];
    group.push(edge);
    edgesByGroup.set(groupKey, group);
  }

  for (const edge of scopedGraph.edges) {
    const groupKey = getAdGraphEdgeGroupKey({
      sourceId: edge.sourceId,
      targetId: edge.targetId,
    });
    const group = edgesByGroup.get(groupKey) ?? [edge];
    const groupPosition = group.findIndex((entry) => entry.id === edge.id);
    const hasSource = flowNodes.some((node) => node.id === edge.sourceId);
    const hasTarget = flowNodes.some((node) => node.id === edge.targetId);
    if (!hasSource || !hasTarget) {
      continue;
    }

    flowEdges.push({
      id: edge.id,
      source: edge.sourceId,
      target: edge.targetId,
      type: 'internalNetworkEdge',
      label: showEdgeLabels ? getAdGraphLinkDisplayLabel(edge) : undefined,
      animated: false,
      data: {
        simplified: simplifyEdges,
        showEdgeLabels,
        isAbusable: edge.isAbusable,
        relationshipType: edge.relationshipType,
        groupPosition: groupPosition >= 0 ? groupPosition : 0,
        groupSize: group.length,
      },
    });
  }

  return { flowNodes, flowEdges };
}

export function getInternalNetworkHighlightNodeIds({
  graph,
  highlightNodeId,
}: {
  graph: AdGraphResponseDto;
  highlightNodeId: string | null;
}) {
  if (!highlightNodeId) {
    return null;
  }

  const highlightedIds = new Set<string>([highlightNodeId]);
  for (const edge of graph.edges) {
    if (edge.sourceId === highlightNodeId) {
      highlightedIds.add(edge.targetId);
    }
    if (edge.targetId === highlightNodeId) {
      highlightedIds.add(edge.sourceId);
    }
  }

  return highlightedIds;
}
